import { useEffect, useState } from "react";
import { Plus, Calendar } from "lucide-react";
import { RichTextPreview } from "@/components/Editor/RichTextPreview";
import { ReleaseNoteModal } from "@/components/admin/ReleaseNoteModal";
import {
  releaseNotesService,
  type ReleaseNote,
} from "@/services/release-notes.service";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";

export default function ReleaseNotes() {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadNotes = async () => {
    try {
      setIsLoading(true);
      const data = await releaseNotesService.list();
      setReleaseNotes(data);
    } catch (e) {
      console.error("Failed to load release notes", e);
    }
    finally{
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  return (
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Release Notes</h1>
          <p className="text-muted-foreground mt-1">
            Send release notes about platform updates to your users
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#d7785b] text-white rounded-lg hover:bg-[#c66a4f] transition-colors"
        >
          <Plus size={20} />
          Create Release Note
        </button>
      </div>

      {/* Release Notes List */}
      <div className="bg-card rounded-xl border border-border">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            Recent Release Notes
          </h2>
        </div>
{isLoading ? <ReleaseNoteSkeleton/>
    :
        <div className="divide-y divide-border">
          {releaseNotes.map((note) => (
            <div
              key={note.id}
              className="p-6 hover:bg-accent transition-colors"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-foreground">
                    {note.title}
                  </h3>
                </div>

                <div className="prose max-w-none">
                  <RichTextPreview content={note.description} />
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground pt-2 border-t border-border">
                  <div className="flex items-center gap-1">
                    <Calendar size={16} />
                    <span>{new Date(note.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>}
      </div>

      <ReleaseNoteModal
        isOpen={isModalOpen}
        isSubmitting={isSubmitting}
        onClose={() => setIsModalOpen(false)}
        onSubmit={async (payload) => {
          try {
            setIsSubmitting(true);
            await releaseNotesService.create(payload);
            setIsModalOpen(false);
            await loadNotes();
          } catch (e) {
            console.error("Failed to create release note", e);
          } finally {
            setIsSubmitting(false);
          }
        }}
      />
    </div>
  );
}

const ReleaseNoteSkeleton = ()=>{
  return <>
  <div className="flex w-full flex-col items-start justify-center">
            {Array.from({ length: 4 }).map((_, index) => (
              <Card key={index} className="w-full animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-1/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-muted rounded w-5/6"></div>
                    <div className="h-3 bg-muted rounded w-5/6"></div>
                    <div className="h-3 bg-muted rounded w-4/6"></div>
                  </div>
                </CardContent>
                <CardFooter>
                  <div className="h-3 bg-muted rounded w-1/6"></div>
                </CardFooter>
              </Card>
            ))}
          </div>
  </>
}
